import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingBag, Search, Plus, Filter, Eye, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Marketplace = () => {
  const { t, i18n } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { value: 'all', label: t('marketplace.categories.all') },
    { value: 'motorcycle', label: t('marketplace.categories.motorcycle') },
    { value: 'parts', label: t('marketplace.categories.parts') },
    { value: 'accessories', label: t('marketplace.categories.accessories') },
    { value: 'gear', label: t('marketplace.categories.gear') },
  ];

  const conditions = [
    { value: 'all', label: t('marketplace.categories.all') },
    { value: 'new', label: t('marketplace.conditions.new') },
    { value: 'like_new', label: t('marketplace.conditions.like_new') },
    { value: 'good', label: t('marketplace.conditions.good') },
    { value: 'fair', label: t('marketplace.conditions.fair') },
  ];

  useEffect(() => {
    fetchListings();
  }, [categoryFilter, conditionFilter]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (conditionFilter !== 'all') params.append('condition', conditionFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_URL}/listings?${params.toString()}`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
  };

  const getCategoryColor = (category) => {
    const colors = {
      motorcycle: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      parts: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      accessories: 'bg-green-500/10 text-green-500 border-green-500/20',
      gear: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  const getConditionLabel = (condition) => {
    return t(`marketplace.conditions.${condition}`);
  };

  const filteredListings = listings.filter(listing =>
    searchQuery === '' ||
    listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    listing.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {t('marketplace.title')}
          </h1>
        </div>
        <Link to="/marketplace/create">
          <Button className="brand-glow font-bold uppercase tracking-wider gap-2" data-testid="create-listing-btn">
            <Plus className="w-4 h-4" />
            {t('marketplace.createListing')}
          </Button>
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={handleSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-zinc-950/50 border-zinc-800"
            data-testid="search-marketplace-input"
          />
        </form>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-12 w-full md:w-[180px]" data-testid="category-filter-select">
            <SelectValue placeholder={i18n.language === 'fr' ? 'Catégorie' : 'Category'} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={conditionFilter} onValueChange={setConditionFilter}>
          <SelectTrigger className="h-12 w-full md:w-[180px]" data-testid="condition-filter-select">
            <SelectValue placeholder={i18n.language === 'fr' ? 'État' : 'Condition'} />
          </SelectTrigger>
          <SelectContent>
            {conditions.map((cond) => (
              <SelectItem key={cond.value} value={cond.value}>
                {cond.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Listings Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Card key={i} className="animate-pulse overflow-hidden">
              <div className="aspect-square bg-muted" />
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-6 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredListings.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredListings.map((listing, index) => (
            <motion.div
              key={listing.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={`/marketplace/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
                <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
                  <div className="relative">
                    <div 
                      className="aspect-square bg-cover bg-center"
                      style={{
                        backgroundImage: listing.images?.[0] 
                          ? `url('${listing.images[0]}')` 
                          : `url('https://images.unsplash.com/photo-1629628083289-009e6f87f5cf?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                      }}
                    />
                    <Badge className={`absolute top-3 left-3 ${getCategoryColor(listing.category)}`}>
                      {t(`marketplace.categories.${listing.category}`)}
                    </Badge>
                    <Badge className="absolute top-3 right-3 bg-black/60 text-white border-0">
                      {getConditionLabel(listing.condition)}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium line-clamp-1 group-hover:text-primary transition-colors">
                      {listing.title}
                    </h3>
                    <p className="text-primary font-mono font-bold text-lg mt-1">
                      {listing.price.toLocaleString()} {listing.currency}
                    </p>
                    <div className="flex items-center justify-between mt-3 text-muted-foreground text-sm">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{listing.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{listing.views}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="bg-card/50">
          <CardContent className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl uppercase">{t('marketplace.noListings')}</h3>
            <p className="text-muted-foreground mt-2">
              {i18n.language === 'fr' 
                ? 'Soyez le premier à publier une annonce !' 
                : 'Be the first to post a listing!'}
            </p>
            <Link to="/marketplace/create">
              <Button className="mt-6" data-testid="create-listing-empty">
                {t('marketplace.createListing')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Marketplace;
